import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import BIPCommercialApartment from '../../assets/data/navigationList/BIPCommercialApartment';
import Asterisk from '../../components/common/Asterisk/Asterisk';
import DatePicker from '../../components/common/DatePicker/DatePicker';
import Dropdown from '../../components/common/Dropdown/Dropdown';
import ErrorMessages from '../../components/common/ErrorMessages/ErrorMessages';
import RadioButton from '../../components/common/RadioGroup/RadioButton';
import ToolTip from '../../components/common/ToolTip/ToolTip';
import { setMenu } from '../../redux/actions/menu/menu';
import {
    getProfileDetails,
    putFiscalDetails,
    resetGetProfileDetails
} from '../../redux/actions/profile/profileDetails';
import { ProfileDetailsState } from '../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../redux/reducers/getSaveData/saveDataReducer';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import {
    selectMenu,
    selectProfileGetDetails,
    selectProfileGetOverview,
    selectSaveData,
    selectSaveResult
} from '../../redux/selectors/exportSelectorPaths';
import {
    FiscalDetailsType,
    initialFiscalDetails,
    keysOfFiscalDetailsType
} from '../../types/profileDetails/FiscalDetailsType';
import { pickFromProfileDetailsType } from '../../types/profileDetails/ProfileDetailsType';
import {
    getFormErrorMessageByName,
    hasError,
    useFormErrorContext
} from '../../utils/context/FormErrorContext';
import { useNavigation } from '../../utils/context/NavigationContext';
import { useReadOnly } from '../../utils/ReadOnlyContext';
import "./IncomeExpense.css";

export default function IncomeExpense(): JSX.Element {
    const dispatch = useDispatch()
    const { clearErrors, errors } = useFormErrorContext()
    const { direction } = useNavigation()
    const { isReadOnly } = useReadOnly()
    const { t } = useTranslation(['contact_info', 'navigation'])
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
    const activeCampaignYear = sessionStorage.getItem("activeCampaignYear")

    const menuState = useSelector(selectMenu);
    const getProfileDetailsState: ProfileDetailsState = useSelector(selectProfileGetDetails)
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)

    const [fiscalDetailsData, setFiscalDetailsData] = useState<FiscalDetailsType>({ ...initialFiscalDetails });
    const [isGetReqSent, setIsGetReqSent] = useState(false);

    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const showOwnerOccupied = ["402", "403", "409", "431", "433", "434", "435", "436"].includes(propertyCode)
        && profileOverview.profile.profile_type_code !== "HTL"

    const dropdownOptions = [
        { value: "", text: "" },
        { value: "1", text: "1" },
        { value: "2", text: "2" },
        { value: "3", text: "3" },
        { value: "4", text: "4" },
        { value: "5", text: "5" },
        { value: "6", text: "6" },
        { value: "7", text: "7" },
        { value: "8", text: "8" },
        { value: "9", text: "9" },
        { value: "10", text: "10" },
        { value: "11", text: "11" }
    ]

    const handleOwnerOccupiedChange = (ownerOccupiedFlag: boolean | null) => {
        if (ownerOccupiedFlag === null) {
            return
        }

        const menuListWithoutCommercialApartment = menuState.menu.filter(menu => !["commercial", "apartment"].includes(menu.name))
        if (ownerOccupiedFlag) {
            dispatch(setMenu(menuListWithoutCommercialApartment))
        } else {
            const incomeExpenseIndex = menuListWithoutCommercialApartment.findIndex(menu => menu.name === "incomeExpense")
            if (incomeExpenseIndex >= 0) {
                const bipCommercialApartment = structuredClone(BIPCommercialApartment);
                for (let i = 0; i < bipCommercialApartment.length; i++) {
                    bipCommercialApartment[i].id = incomeExpenseIndex + 2 + i;
                    const subItems = bipCommercialApartment[i].subItems
                    if (subItems) {
                        for (let j = 0; j < subItems.length; j++) {
                            subItems[j].id = parseInt(String(incomeExpenseIndex + 2 + i) + String((j + 1)))
                        }
                    }
                }
                const newMenuList = menuListWithoutCommercialApartment.slice(0, incomeExpenseIndex + 1)
                    .concat(bipCommercialApartment)
                    .concat(menuListWithoutCommercialApartment.slice(incomeExpenseIndex + 1))
                const isModifiedSubmission = sessionStorage.getItem("isInternalUser") && sessionStorage.getItem("editMode");
                if (isModifiedSubmission) {
                    const commercialItem = newMenuList.find(menu => menu.name === "commercial");
                    if (commercialItem) {
                        if (commercialItem.subItems) {
                            const leaseInfoItem = commercialItem.subItems.find(menu => menu.name === "leaseInformationTable");
                            if (leaseInfoItem) {
                                leaseInfoItem.componentName = "LeaseInfoInternal"
                            }
                        }
                    }
                }
                dispatch(setMenu(newMenuList))
            }
        }
    }

    const handleInputChange = (e: React.ChangeEvent) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement;
        target.value = target.value.trim();
        const targetName = target.name as string;
        if (targetName === "ownerOccupiedFlag") {
            const ownerOccupiedFlag = (target.value === "true" ? true : (target.value === "false" ? false : null))
            setFiscalDetailsData({ ...fiscalDetailsData, [targetName]: ownerOccupiedFlag });
            handleOwnerOccupiedChange(ownerOccupiedFlag)
        } else {
            setFiscalDetailsData({ ...fiscalDetailsData, [targetName]: target.value });
        }
    }

    const handleDateChange = (date: Date | null | undefined, name: string) => {
        if (typeof name !== "string") {
            return;
        }
        try {
            const dateValue = (date ? format(date, "yyyy-MM-dd") : "");
            setFiscalDetailsData({ ...fiscalDetailsData, [name]: dateValue });
        }
        catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        dispatch(resetGetProfileDetails());
        dispatch(getProfileDetails(currentSnapshotId));
        setIsGetReqSent(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isGetReqSent) {
            if (!getProfileDetailsState.loading && !getProfileDetailsState.success) {
                toast.error('Failed to load data');
            } else {
                setFiscalDetailsData(pickFromProfileDetailsType(getProfileDetailsState.data, keysOfFiscalDetailsType))
            }
        }
    }, [isGetReqSent, getProfileDetailsState])

    useEffect(() => {
        if (saveData.saving && !isReadOnly) {
            clearErrors();

            const triggerSpectral = isInternal || direction !== "backward"

            dispatch(putFiscalDetails(currentSnapshotId, {
                "fiscal_details": fiscalDetailsData,
                "validation_campaign_year": activeCampaignYear ?? profileOverview.profile.current_campaign.year.toString(),
                "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-global-pie",
                "trigger_spectral": triggerSpectral,
                "property_code": propertyCode,
                "is_internal": isInternal
            }))
        }
    }, [saveData.saving, isReadOnly, clearErrors, currentSnapshotId, dispatch, fiscalDetailsData, profileOverview.profile.profile_type_code, direction, propertyCode, isInternal, activeCampaignYear, profileOverview.profile.current_campaign.year])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(resetGetProfileDetails());
            dispatch(getProfileDetails(currentSnapshotId))
        }
    }, [currentSnapshotId, dispatch, saveResultState.status]);

    return (
        <>
            <div className='partial-width'>
                <ErrorMessages errors={errors} />
                    <table role='presentation' className="income-expense-table">
                        <tbody>
                            <tr>
                                <td>1.</td>
                                <td>
                                    <label htmlFor='contact-fiscal-year-end-input'>
                                        {t("fiscal")} <Asterisk />
                                    </label>
                                </td>
                                <td>
                                    <DatePicker id='contact-fiscal-year-end' className='contact-fiscal-year-end' name='fiscalYearEndDate'
                                        date={fiscalDetailsData.fiscalYearEndDate} minDate={parseISO("1990-01-01")} maxDate={new Date()}
                                        errorMessage={getFormErrorMessageByName(errors, "fiscalYearEndDate")} onChange={handleDateChange}
                                        hasError={hasError(errors, "fiscalYearEndDate")} />
                                </td>
                            </tr>
                            <tr>
                                <td>2.</td>
                                <td>
                                    <label htmlFor='contact-partial-year'>
                                        <ToolTip contents={t("partialYear")}>
                                            {t("partialYearTooltip")}
                                        </ToolTip>
                                    </label>
                                </td>
                                <td>
                                    <Dropdown id='contact-partial-year'
                                    className='contact-partial-year'    
                                    name='partialIncomeMonth' 
                                    options={dropdownOptions}
                                    value={fiscalDetailsData.partialIncomeMonth ?? ""}
                                    onChange={handleInputChange} />
                            </td>
                        </tr>
                        {
                            showOwnerOccupied &&
                            <tr>
                                <td>3.</td>
                                <td>
                                    {t("occupied")} <Asterisk />
                                </td>
                                <td>
                                    <span className={'side-by-side contact-owner-occupied-container' + (hasError(errors, "ownerOccupiedFlag") ? ' radio-error' : '')}>
                                        <RadioButton name="ownerOccupiedFlag" value="true" id='contact-occupied-no' text={t("no")} textPosition='right'
                                            currentValue={fiscalDetailsData.ownerOccupiedFlag === null ? "" : fiscalDetailsData.ownerOccupiedFlag}
                                            onChange={handleInputChange}
                                        />
                                        <RadioButton name="ownerOccupiedFlag" value="false" id='contact-occupied-yes' text={t("yes")} textPosition='right'
                                            currentValue={fiscalDetailsData.ownerOccupiedFlag === null ? "" : fiscalDetailsData.ownerOccupiedFlag}
                                            onChange={handleInputChange}
                                        />
                                    </span>
                                    {hasError(errors, "ownerOccupiedFlag") ? <div className="input-error-message">{getFormErrorMessageByName(errors, "ownerOccupiedFlag")}</div> : ""}
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}

